import { dateUtil } from '@salescore/buff-common'
import type { BusinessDayFieldsFragment } from '@salescore/client-api'
import type { KpiDateDimension, ProgressType } from '@salescore/client-common'
import { CORE_CONSTANT, timeframeSchema } from '@salescore/core'
import type { Dayjs } from 'dayjs'
import { Array, Order, pipe, Record } from 'effect'

import type { KpiPivotConfig } from '../../context'
import { generateDatesWithHoliday } from './generateDates'

const calculateProgressRate = (businessDays: number, elapsedDays: number, isDemo: boolean) => {
  if (isDemo) {
    return 0.33
  }

  if (businessDays === 0) {
    return 0
  }

  return (elapsedDays * 1) / businessDays
}

const timeframes = ['day', 'week', 'month', 'fiscal_quarter', 'fiscal_year', 'year']

//
// startAtからendAtの間に含まれる営業日・経過日を計算し、進捗率(経過日/営業日)を求める関数
//

export const calculateAverageProgressService = ({
  startAt,
  endAt,
  startOfToday,
  isDemo,
  pivot,
  businessDays,
}: {
  startAt: Dayjs | undefined
  endAt: Dayjs | undefined
  startOfToday: Dayjs
  isDemo?: boolean
  pivot: KpiPivotConfig
  businessDays: BusinessDayFieldsFragment[]
}): ProgressType => {
  //
  // 進捗率を求める計算は、基本的にはstartAt〜endAtに含まれる営業日などを求めるだけで終わりなのだが、
  // 日付軸を使っている時は、行（列）ごとに個別の進捗率計算を行う必要があり、日付軸関連の情報が必要になる
  // 例えば、2023年9月20日に「全期間」で「行：KPI日付(月)」で分析している場合、
  // 「2023-08」の行は進捗率100%扱いにするし、「2023-09」の行は66%だし、「2023-10」の行は0%である
  //
  const kpiDateDimensions = pipe(
    [
      ...pivot.rows.map((x, index) => ({ ...x, index, type: 'row' as const })),
      ...pivot.columns.map((x, index) => ({ ...x, index, type: 'column' as const })),
    ],
    // eslint-disable-next-line sonarjs/todo-tag
    Array.filter((x) => x.key.endsWith(CORE_CONSTANT.KPI_PIVOT_KPI_DATE_DIMENSION().key)), // TODO
    Array.flatMapNullishOr((x): KpiDateDimension | undefined => {
      if (x.key === CORE_CONSTANT.KPI_PIVOT_KPI_DATE_DIMENSION().key) {
        return {
          ...x,
          // eslint-disable-next-line sonarjs/todo-tag
          timeframe: 'month' as const, // dateSpanがないとき、デフォルトだとmonth // TODO
          // eslint-disable-next-line sonarjs/todo-tag
          timeframeRank: timeframes.indexOf('month'), // TODO
        }
      }
      const maybeTimeframe = x.key.replace(`_${CORE_CONSTANT.KPI_PIVOT_KPI_DATE_DIMENSION().key}`, ``)
      // eslint-disable-next-line sonarjs/todo-tag
      const timeframe = timeframeSchema.safeParse(maybeTimeframe) // TODO
      if (!timeframe.success) {
        return undefined
      }
      return {
        ...x,
        timeframe: timeframe.data,
        // eslint-disable-next-line sonarjs/todo-tag
        timeframeRank: timeframes.indexOf(timeframe.data), // TODO
      }
    }),
    Array.sortBy(Order.mapInput(Order.Number, (x) => x.timeframeRank)),
  )
  const hasTimeRelatedDimension = kpiDateDimensions.length > 0

  // 全期間のときは、進捗率100%扱いにする
  if (startAt === undefined || endAt === undefined) {
    return {
      elapsedDays: 0,
      businessDays: 0,
      progressRate: 1,
      progressPercent: 100,
      hasTimeRelatedDimension,
      kpiDateDimensions,
      daysByTimeframe: {},
    }
  }
  const dates = generateDatesWithHoliday({ startAt, endAt, businessDays })
  const businessDates = dates.filter((date) => date.is_business_day)
  const elapsedDates = businessDates.filter(
    (date) => date.date.isBefore(startOfToday, 'day') || date.date.isSame(startOfToday, 'day'),
  )
  const progressRate = calculateProgressRate(businessDates.length, elapsedDates.length, isDemo === true)

  return {
    elapsedDays: elapsedDates.length,
    businessDays: businessDates.length,
    progressRate,
    progressPercent: Math.round(progressRate * 100),
    hasTimeRelatedDimension,
    kpiDateDimensions,
    daysByTimeframe: Record.fromIterableWith(kpiDateDimensions, (d) => {
      const elapsedDays = pipe(
        elapsedDates,
        Array.groupBy(
          (x) =>
            // eslint-disable-next-line sonarjs/todo-tag
            // TODO: weekの始まりの日の統一
            // if (timeRelatedDimensionRowIndex.type === 'week') {
            //   return x.date.startOf('week').add(1, 'day').format(timeRelatedDimensionRowIndex.format)
            // }
            // eslint-disable-next-line sonarjs/todo-tag
            // TODO: timeframeの統一
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-type-assertion
            dateUtil.format(x.date, d.timeframe as any) ?? '',
        ),
        Record.map((xs) => xs.length),
      )
      const businessDays = pipe(
        businessDates,
        Array.groupBy(
          (x) =>
            // 同上
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-type-assertion
            dateUtil.format(x.date, d.timeframe as any) ?? '',
        ),
        Record.map((xs) => xs.length),
      )
      return [
        d.timeframe,
        {
          elapsedDays,
          businessDays,
        },
      ]
    }),
  }
}
